import type { Routes } from '@angular/router';

import { acceptInviteParamGuard } from './core/guards/accept-invite-param-guard';
import { adaRampAssetAccessCanMatchGuard } from './core/guards/ada-ramp-asset-access-guard';
import { authGuard, authGuardChild } from './core/guards/auth-guard';
import { curbAssetAccessCanMatchGuard } from './core/guards/curb-asset-access-guard';
import { featureFlagGuard } from './core/guards/feature-flag-guard';
import { forceMainAssetAccessCanMatchGuard } from './core/guards/force-main-asset-access-guard';
import { gravelRoadAssetAccessCanMatchGuard } from './core/guards/gravel-road-asset-access-guard';
import { gravityMainAssetAccessCanMatchGuard } from './core/guards/gravity-main-asset-access-guard';
import { importAccessCanMatchGuard } from './core/guards/import-access-guard';
import { manholeAssetAccessCanMatchGuard } from './core/guards/manhole-asset-access-guard';
import { pavedParkingLotAssetAccessCanMatchGuard } from './core/guards/paved-parking-lot-asset-access-guard';
import { pavedRoadAssetAccessCanMatchGuard } from './core/guards/paved-road-asset-access-guard';
import { planAccessCanMatchGuard } from './core/guards/plan-access-guard';
import { planProjectAccessCanMatchGuard } from './core/guards/plan-project-access-guard';
import { productRoleGuard } from './core/guards/product-role-guard';
import { recommendationAccessCanMatchGuard } from './core/guards/recommendation-access-guard';
import { sidewalkAssetAccessCanMatchGuard } from './core/guards/sidewalk-asset-access-guard';
import { signAssetAccessCanMatchGuard } from './core/guards/sign-asset-access-guard';
import { stormDrainageAssetAccessCanMatchGuard } from './core/guards/storm-drainage-asset-access-guard';
import { stripingAssetAccessCanMatchGuard } from './core/guards/striping-asset-access-guard';
import { tenantLandingGuard } from './core/guards/tenant-landing-guard';
import { tenantMembershipGuard } from './core/guards/tenant-membership-guard';
import { tenantRoleGuard } from './core/guards/tenant-role-guard';
import { unpavedRoadAssetAccessCanMatchGuard } from './core/guards/unpaved-road-asset-access-guard';
import { valveAssetAccessCanMatchGuard } from './core/guards/valve-asset-access-guard';
import { waterMainAssetAccessCanMatchGuard } from './core/guards/water-main-asset-access-guard';
import { AuthEntraExternalIdSsoComponent } from './routes/auth/auth-entra-external-id-sso';
import { AuthEntraIdSsoComponent } from './routes/auth/auth-entra-id-sso';

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: '/o',
  },
  {
    path: 'auth/entra-id-sso',
    component: AuthEntraIdSsoComponent, // Do not lazy load, this handles auth redirects
  },
  {
    path: 'auth/entra-external-id-sso',
    component: AuthEntraExternalIdSsoComponent, // Do not lazy load, this handles auth redirects
  },
  {
    path: 'o', // Organization-scoped routes
    canActivate: [authGuard],
    canActivateChild: [authGuardChild],
    title: 'Asset Agent',
    children: [
      {
        path: '',
        canActivate: [tenantLandingGuard],
        loadComponent: () =>
          import('./routes/organizations/organizations-page').then(
            (m) => m.UserOrganizationsPageComponent,
          ),
        title: 'Asset Agent - Organizations',
      },
      {
        path: ':tenantId',
        loadComponent: () =>
          import('./routes/organizations/organization-landing-page').then(
            (m) => m.OrganizationLandingPageComponent,
          ),
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'dashboard',
          },
          {
            path: 'dashboard',
            canMatch: [tenantMembershipGuard()],
            title: 'Asset Agent - Dashboard',
            loadComponent: () =>
              import('./routes/organizations/dashboard/dashboard').then(
                (m) => m.DashboardComponent,
              ),
          },
          {
            path: 'assets',
            canMatch: [tenantMembershipGuard()],
            children: [
              {
                path: '',
                title: 'Asset Agent - Assets',
                loadComponent: () =>
                  import('./routes/organizations/assets/assets').then(
                    (m) => m.AssetsComponent,
                  ),
              },
              {
                path: 'paved-parking-lots/:assetId',
                canMatch: [pavedParkingLotAssetAccessCanMatchGuard],
                children: [
                  {
                    path: '',
                    canActivate: [tenantRoleGuard(['AssetRead'])],
                    loadComponent: () =>
                      import('./routes/organizations/assets/details/paved-parking-lot-asset-details').then(
                        (m) => m.PavedParkingLotAssetDetailsComponent,
                      ),
                    title: 'Asset Agent - Paved Parking Lot Asset Details',
                  },
                ],
              },
              {
                path: 'paved-roads/:assetId',
                canMatch: [pavedRoadAssetAccessCanMatchGuard],
                children: [
                  {
                    path: '',
                    canActivate: [tenantRoleGuard(['AssetRead'])],
                    loadComponent: () =>
                      import('./routes/organizations/assets/details/paved-road-asset-details').then(
                        (m) => m.PavedRoadAssetDetailsComponent,
                      ),
                    title: 'Asset Agent - Paved Road Asset Details',
                  },
                ],
              },
              {
                path: 'unpaved-roads/:assetId',
                canMatch: [unpavedRoadAssetAccessCanMatchGuard],
                children: [
                  {
                    path: '',
                    canActivate: [tenantRoleGuard(['AssetRead'])],
                    loadComponent: () =>
                      import('./routes/organizations/assets/details/unpaved-road-asset-details').then(
                        (m) => m.UnpavedRoadAssetDetailsComponent,
                      ),
                    title: 'Asset Agent - Unpaved Road Asset Details',
                  },
                ],
              },
              {
                path: 'gravel-roads/:assetId',
                canMatch: [gravelRoadAssetAccessCanMatchGuard],
                children: [
                  {
                    path: '',
                    canActivate: [tenantRoleGuard(['AssetRead'])],
                    loadComponent: () =>
                      import('./routes/organizations/assets/details/gravel-road-asset-details').then(
                        (m) => m.GravelRoadAssetDetailsComponent,
                      ),
                    title: 'Asset Agent - Gravel Road Asset Details',
                  },
                ],
              },
              {
                path: 'gravity-mains/:assetId',
                canMatch: [gravityMainAssetAccessCanMatchGuard],
                children: [
                  {
                    path: '',
                    canActivate: [tenantRoleGuard(['AssetRead'])],
                    loadComponent: () =>
                      import('./routes/organizations/assets/details/gravity-main-asset-details').then(
                        (m) => m.GravityMainAssetDetailsComponent,
                      ),
                    title: 'Asset Agent - Gravity Main Asset Details',
                  },
                ],
              },
              {
                path: 'force-mains/:assetId',
                canMatch: [forceMainAssetAccessCanMatchGuard],
                children: [
                  {
                    path: '',
                    canActivate: [tenantRoleGuard(['AssetRead'])],
                    loadComponent: () =>
                      import('./routes/organizations/assets/details/force-main-asset-details').then(
                        (m) => m.ForceMainAssetDetailsComponent,
                      ),
                    title: 'Asset Agent - Force Main Asset Details',
                  },
                ],
              },
              {
                path: 'water-mains/:assetId',
                canMatch: [waterMainAssetAccessCanMatchGuard],
                children: [
                  {
                    path: '',
                    canActivate: [tenantRoleGuard(['AssetRead'])],
                    loadComponent: () =>
                      import('./routes/organizations/assets/details/water-main-asset-details').then(
                        (m) => m.WaterMainAssetDetailsComponent,
                      ),
                    title: 'Asset Agent - Water Main Asset Details',
                  },
                ],
              },
              {
                path: 'valves/:assetId',
                canMatch: [
                  featureFlagGuard('enableValves'),
                  valveAssetAccessCanMatchGuard,
                ],
                children: [
                  {
                    path: '',
                    canActivate: [tenantRoleGuard(['AssetRead'])],
                    loadComponent: () =>
                      import('./routes/organizations/assets/details/valve-asset-details').then(
                        (m) => m.ValveAssetDetailsComponent,
                      ),
                    title: 'Asset Agent - Valve Asset Details',
                  },
                ],
              },
              {
                path: 'manholes/:assetId',
                canMatch: [
                  featureFlagGuard('enableManholes'),
                  manholeAssetAccessCanMatchGuard,
                ],
                children: [
                  {
                    path: '',
                    canActivate: [tenantRoleGuard(['AssetRead'])],
                    loadComponent: () =>
                      import('./routes/organizations/assets/details/manhole-asset-details').then(
                        (m) => m.ManholeAssetDetailsComponent,
                      ),
                    title: 'Asset Agent - Manhole Asset Details',
                  },
                ],
              },
              {
                path: 'ada-ramps/:assetId',
                canMatch: [
                  featureFlagGuard('enableAdaRamps'),
                  adaRampAssetAccessCanMatchGuard,
                ],
                children: [
                  {
                    path: '',
                    canActivate: [tenantRoleGuard(['AssetRead'])],
                    loadComponent: () =>
                      import('./routes/organizations/assets/details/ada-ramp-asset-details').then(
                        (m) => m.AdaRampAssetDetailsComponent,
                      ),
                    title: 'Asset Agent - ADA Ramp Asset Details',
                  },
                ],
              },
              {
                path: 'sidewalks/:assetId',
                canMatch: [
                  featureFlagGuard('enableSidewalks'),
                  sidewalkAssetAccessCanMatchGuard,
                ],
                children: [
                  {
                    path: '',
                    canActivate: [tenantRoleGuard(['AssetRead'])],
                    loadComponent: () =>
                      import('./routes/organizations/assets/details/sidewalk-asset-details').then(
                        (m) => m.SidewalkAssetDetailsComponent,
                      ),
                    title: 'Asset Agent - Sidewalk Asset Details',
                  },
                ],
              },
              {
                path: 'storm-drainage/:assetId',
                canMatch: [
                  featureFlagGuard('enableStormDrainage'),
                  stormDrainageAssetAccessCanMatchGuard,
                ],
                children: [
                  {
                    path: '',
                    canActivate: [tenantRoleGuard(['AssetRead'])],
                    loadComponent: () =>
                      import('./routes/organizations/assets/details/storm-drainage-asset-details').then(
                        (m) => m.StormDrainageAssetDetailsComponent,
                      ),
                    title: 'Asset Agent - Storm Drainage Asset Details',
                  },
                ],
              },
              {
                path: 'curbs/:assetId',
                canMatch: [
                  featureFlagGuard('enableCurbs'),
                  curbAssetAccessCanMatchGuard,
                ],
                children: [
                  {
                    path: '',
                    canActivate: [tenantRoleGuard(['AssetRead'])],
                    loadComponent: () =>
                      import('./routes/organizations/assets/details/curb-asset-details').then(
                        (m) => m.CurbAssetDetailsComponent,
                      ),
                    title: 'Asset Agent - Curb Asset Details',
                  },
                ],
              },
              {
                path: 'signs/:assetId',
                canMatch: [
                  featureFlagGuard('enableSignage'),
                  signAssetAccessCanMatchGuard,
                ],
                children: [
                  {
                    path: '',
                    canActivate: [tenantRoleGuard(['AssetRead'])],
                    loadComponent: () =>
                      import('./routes/organizations/assets/details/sign-asset-details').then(
                        (m) => m.SignAssetDetailsComponent,
                      ),
                    title: 'Asset Agent - Sign Asset Details',
                  },
                ],
              },
              {
                path: 'striping/:assetId',
                canMatch: [
                  featureFlagGuard('enableStriping'),
                  stripingAssetAccessCanMatchGuard,
                ],
                children: [
                  {
                    path: '',
                    canActivate: [tenantRoleGuard(['AssetRead'])],
                    loadComponent: () =>
                      import('./routes/organizations/assets/details/striping-asset-details').then(
                        (m) => m.StripingAssetDetailsComponent,
                      ),
                    title: 'Asset Agent - Striping Asset Details',
                  },
                ],
              },
            ],
          },
          {
            path: 'configuration',
            canMatch: [tenantMembershipGuard()],
            title: 'Asset Agent - Asset Configuration',
            loadComponent: () =>
              import('./routes/organizations/configuration/asset-configuration').then(
                (m) => m.AssetConfigurationComponent,
              ),
          },
          {
            path: 'recommendations',
            canMatch: [tenantMembershipGuard()],
            children: [
              {
                path: '',
                canActivate: [tenantRoleGuard(['RecommendationsRead'])],
                title: 'Asset Agent - Recommendations',
                loadComponent: () =>
                  import('./routes/organizations/recommendations/recommendations').then(
                    (m) => m.RecommendationsComponent,
                  ),
              },
              {
                path: ':recommendationSetId',
                canMatch: [recommendationAccessCanMatchGuard],
                children: [
                  {
                    path: '',
                    canActivate: [tenantRoleGuard(['RecommendationsRead'])],
                    title: 'Asset Agent - Recommendation Details',
                    loadComponent: () =>
                      import('./routes/organizations/recommendations/recommendation-details/recommendation-details').then(
                        (m) => m.RecommendationDetailsComponent,
                      ),
                  },
                  {
                    path: 'edit',
                    canActivate: [tenantRoleGuard(['RecommendationsUpdate'])],
                    loadComponent: () =>
                      import('./routes/organizations/recommendations/edit-recommendation-details/edit-recommendation-details').then(
                        (m) => m.EditRecommendationDetailsComponent,
                      ),
                    title: 'Asset Agent - Edit Recommendation',
                  },
                ],
              },
            ],
          },
          {
            path: 'plans',
            canMatch: [
              featureFlagGuard('enablePlanningStudio'),
              tenantMembershipGuard(),
            ],
            children: [
              {
                path: '',
                canActivate: [tenantRoleGuard(['PlanRead'])],
                title: 'Asset Agent - Planning Studio',
                loadComponent: () =>
                  import('./routes/organizations/plans/plans-landing-page').then(
                    (m) => m.PlansComponent,
                  ),
              },
              {
                path: 'compare',
                canMatch: [featureFlagGuard('enablePlanComparison')],
                canActivate: [tenantRoleGuard(['PlanRead'])],
                title: 'Asset Agent - Plan Comparison',
                loadComponent: () =>
                  import('./routes/organizations/plans/compare-plans/compare-plans').then(
                    (m) => m.ComparePlansComponent,
                  ),
              },
              {
                path: ':planId',
                canMatch: [planAccessCanMatchGuard],
                children: [
                  {
                    path: '',
                    canActivate: [tenantRoleGuard(['PlanRead'])],
                    title: 'Asset Agent - Plan Details',
                    loadComponent: () =>
                      import('./routes/organizations/plans/plan-details/plan-details').then(
                        (m) => m.PlanDetailsComponent,
                      ),
                  },
                  {
                    path: 'projects',
                    canActivate: [tenantRoleGuard(['PlanRead'])],
                    children: [
                      {
                        path: ':planProjectId',
                        canMatch: [planProjectAccessCanMatchGuard],
                        title: 'Asset Agent - Project Details',
                        children: [
                          {
                            path: '',
                            loadComponent: () =>
                              import('./routes/organizations/plans/project-details/project-details').then(
                                (m) => m.ProjectDetailsComponent,
                              ),
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: 'analysis',
            title: 'Asset Agent - Insights',
            canMatch: [
              featureFlagGuard('enableScenariosAndAnalysis'),
              tenantMembershipGuard(),
            ],
            loadComponent: () =>
              import('./routes/organizations/analysis/analysis').then(
                (m) => m.Analysis,
              ),
          },
          {
            path: 'imports',
            canMatch: [tenantMembershipGuard()],
            children: [
              {
                path: '',
                title: 'Asset Agent - Import Data',
                canActivate: [tenantRoleGuard(['AssetImportRead'])],
                loadComponent: () =>
                  import('./routes/organizations/imports/imports').then(
                    (m) => m.ImportsComponent,
                  ),
              },
              {
                path: 'wizard',
                title: 'Asset Agent - Import Wizard',
                canActivate: [tenantRoleGuard(['AssetImportWrite'])],
                loadComponent: () =>
                  import('./routes/organizations/imports/wizard/import-wizard').then(
                    (m) => m.ImportWizardComponent,
                  ),
              },
              {
                path: ':importId',
                canMatch: [importAccessCanMatchGuard],
                canActivate: [tenantRoleGuard(['AssetImportRead'])],
                title: 'Asset Agent - Import Details',
                loadComponent: () =>
                  import('./routes/organizations/imports/import-details').then(
                    (m) => m.ImportDetailsComponent,
                  ),
              },
            ],
          },
          {
            path: 'exports',
            canMatch: [tenantMembershipGuard()],
            children: [
              {
                path: '',
                title: 'Asset Agent - Export Data',
                canActivate: [tenantRoleGuard(['ExportRead'])],
                loadComponent: () =>
                  import('./routes/organizations/exports/exports').then(
                    (m) => m.ExportsComponent,
                  ),
              },
              {
                path: ':exportId',
                title: 'Asset Agent - Export Details',
                canActivate: [tenantRoleGuard(['ExportRead'])],
                loadComponent: () =>
                  import('./routes/organizations/exports/export-details').then(
                    (m) => m.ExportDetailsComponent,
                  ),
              },
            ],
          },
          {
            path: 'users',
            canMatch: [tenantMembershipGuard()],
            canActivate: [tenantRoleGuard(['OrganizationUserList'])],
            title: 'Asset Agent - User Management',
            loadComponent: () =>
              import('./routes/organizations/users/users').then(
                (m) => m.UsersComponent,
              ),
          },
          {
            path: 'no-access', // don't check for tenant membership here
            loadComponent: () =>
              import('./routes/organizations/no-access/no-access').then(
                (m) => m.OrganizationNoAccessPageComponent,
              ),
            title: 'Asset Agent - No Access',
          },
          {
            path: '**',
            canMatch: [tenantMembershipGuard(false)],
            loadComponent: () =>
              import('#/app/routes/organizations/page-not-found/page-not-found').then(
                (m) => m.OrganizationPageNotFoundComponent,
              ),
            title: 'Asset Agent - Page Not Found',
          },
          {
            path: '**', // don't check for tenant membership here
            loadComponent: () =>
              import('./routes/organizations/no-access/no-access').then(
                (m) => m.OrganizationNoAccessPageComponent,
              ),
            title: 'Asset Agent - No Access',
          },
        ],
      },
    ],
  },
  {
    path: 'admin',
    canActivate: [authGuard],
    canActivateChild: [authGuardChild],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'organizations',
      },
      {
        path: 'organizations',
        canActivate: [productRoleGuard],
        title: 'Asset Agent - Organizations',
        loadComponent: () =>
          import('./routes/admin/organizations/admin-organizations').then(
            (m) => m.AdminOrganizationsComponent,
          ),
      },
      {
        path: 'no-access',
        loadComponent: () =>
          import('./routes/admin/no-access/no-access').then(
            (m) => m.AdminNoAccessPageComponent,
          ),
        title: 'Asset Agent - No Access',
      },
    ],
  },
  {
    path: 'auth',
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'login',
      },
      {
        path: 'login',
        title: 'Asset Agent - Sign In',
        loadComponent: () =>
          import('./routes/auth/login/auth-login').then(
            (m) => m.AuthLoginComponent,
          ),
      },
      {
        path: 'login-failed',
        title: 'Asset Agent - Sign In Failed',
        loadComponent: () =>
          import('./routes/auth/login-failed/auth-login-failed').then(
            (m) => m.AuthLoginFailedComponent,
          ),
      },
      {
        path: 'logout',
        title: 'Asset Agent - Signing Out',
        loadComponent: () =>
          import('./routes/auth/logout/auth-logout').then(
            (m) => m.AuthLogoutComponent,
          ),
      },
      {
        path: 'logged-out',
        title: 'Asset Agent - Signed Out',
        loadComponent: () =>
          import('./routes/auth/logged-out/auth-logged-out').then(
            (m) => m.AuthLoggedOutComponent,
          ),
      },
    ],
  },
  {
    path: 'accept-invite',
    canMatch: [acceptInviteParamGuard],
    loadComponent: () =>
      import('./routes/accept-invite/accept-invite').then(
        (m) => m.AcceptInviteComponent,
      ),
    title: 'Asset Agent - Accept Invitation',
  },
  {
    path: 'no-access',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./routes/no-access/no-access').then(
        (m) => m.NoAccessPageComponent,
      ),
    title: 'Asset Agent - No Access',
  },
  {
    path: '**',
    loadComponent: () =>
      import('#/app/routes/page-not-found/page-not-found').then(
        (m) => m.PageNotFoundComponent,
      ),
    title: 'Asset Agent - Page Not Found',
  },
];
